import { useDispatch, useSelector } from "react-redux";
import { BuilderProduct, BuilderRoomAsset, DragSession, Project, Surface, SurfaceGroup, Texture } from "@/store/builderSlice";
import { useTexture, Text, Line } from '@react-three/drei';
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from 'three';
import { MeshCollider, RigidBody } from "@react-three/rapier";
import BuilderSelct from "@/store/builderStoreSelector";
import { loadGLTF, loadTexture } from "@/utils/builder/floorTextureUtils";
import { DimensionalViewType, SurfaceType } from "@/utils/builder/builderConstants";
import { actions as builderActions } from "@/store/builderSlice";
import { CSG } from 'three-csg-ts';
import { useFrame, useThree, useLoader } from "@react-three/fiber";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { DoorOrWindowMesh, RoomAssetOffsets } from "./Walls";
import { getDefaultWallTexture, getDefaultWallNormalTexture } from "@/utils/builder/wallTextureUtils";


export interface WallProps {
    isThinWall: boolean;
    isWallRotated: boolean;
    wallMesh: THREE.Mesh;
    doorMeshes: DoorOrWindowMesh[];
    windowMeshes: DoorOrWindowMesh[];
    wallDimensions: number[];
    hasFurnitureModels: boolean;
    // this is to indicate room model was scanned with newer version of app and 
    //has door/window holes created, furniture included in it
}

const Wall = (props: WallProps) => {
    const meshRef = useRef<THREE.Mesh>(null);
    const hiddenMeshRef = useRef<THREE.Mesh>(null);
    const wallTexture = useRef(null);
    const wallNormalTexture = useRef(null);
    const currentWallTextureUrl = useRef<string | undefined>(undefined);
    const dispatch = useDispatch();
    const [textureLoaded, setTextureLoaded] = useState(false);
    const scene = useThree((state) => state.scene);
    const camera = useThree((state) => state.camera);
    const cameraDirRef = useRef(new THREE.Vector3(0, 0, 0));
    const wallMesh = props.wallMesh;
    const doorMeshes: DoorOrWindowMesh[] = props.doorMeshes,
        windowMeshes: DoorOrWindowMesh[] = props.windowMeshes,
        surfaceId: string = wallMesh.name;

    const wallHideCount = useRef(0);
    let surface: Surface | undefined;
    let textureObj: Texture | undefined;
    const is2DView: boolean = BuilderSelct.ui().dimensionalView === DimensionalViewType.TWO_D;
    const is2DViewRef = useRef(false);
    is2DViewRef.current = is2DView;

    // SM Performance Improvements - Oct 9 - as per -> (https://r3f.docs.pmnd.rs/advanced/pitfalls)
    const rayCaster = new THREE.Raycaster();
    const cameraDir = new THREE.Vector3();

    useFrame(() => {
        if (!hiddenMeshRef.current || !meshRef.current || is2DViewRef.current) return;
        camera.getWorldDirection(cameraDir);

        // if (cameraDirRef.current && Math.abs(cameraDir.x - cameraDirRef.current.x) < 0.05 &&
        //     Math.abs(cameraDir.y - cameraDirRef.current.y) < 0.05 && Math.abs(cameraDir.z - cameraDirRef.current.z) < 0.05)
        //     return;

        cameraDirRef.current = cameraDir;
        const meshNormal = new THREE.Vector3();
        hiddenMeshRef.current.getWorldDirection(meshNormal);

        const pos = new THREE.Vector3();
        meshRef.current.getWorldPosition(pos);
        const direction = new THREE.Vector3();
        direction.copy(pos).sub(camera.position).normalize();
        let clickMouse = new THREE.Vector2();
        rayCaster.setFromCamera(clickMouse, camera);
        rayCaster.set(camera.position, direction);
        const intersections = rayCaster.intersectObject(scene, true);

        const wallNames: string[] = [];
        let currentMesh: THREE.Mesh | null = null;
        let windowsAndDoorsGroup: THREE.Group | null = null;
        let wallSeperationLinesGroup: THREE.Group | null = null;
        let wallBaseboardGroup: THREE.Group | null = null;
        scene.traverse((child: any) => {
            if (child.userData.type && child.userData.type === surfaceId) {
                currentMesh = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_windows_doors') {
                windowsAndDoorsGroup = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_wall_seperation_lines') {
                wallSeperationLinesGroup = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_wall_baseboard') {
                wallBaseboardGroup = child;
            }
        });
        let hideWall = false;
        if (intersections.length > 0) {
            intersections.forEach(intersection => {
                if (intersection.object.type === 'Mesh' && intersection.object.userData.wtype) {
                    wallNames.push(intersection.object.userData.wtype);
                }
            });
            hideWall = !wallNames.includes(surfaceId);
        }
        if (hideWall) wallHideCount.current = wallHideCount.current + 1;
        else wallHideCount.current = 0;

        if (currentMesh)
            (currentMesh as THREE.Mesh).visible = !(wallHideCount.current > 3);
        if (windowsAndDoorsGroup)
            (windowsAndDoorsGroup as THREE.Group).visible = !(wallHideCount.current > 3);
        if (wallSeperationLinesGroup)
            // (wallSeperationLinesGroup as THREE.Group).visible = !(wallHideCount.current > 3);
            (wallSeperationLinesGroup as THREE.Group).visible = false; // temporary change for testing
        if (wallBaseboardGroup)
            // (wallBaseboardGroup as THREE.Group).visible = !(wallHideCount.current > 3);
            (wallBaseboardGroup as THREE.Group).visible = false; // temporary change for testing
    });

    useEffect(() => {
        if (textureObj && textureObj.type === 'color') {
            updateWallColor(textureObj.hexCode!);
        }
    }, [textureObj]);

    const createHoleOnWall = (mainMesh: THREE.Mesh, holeMesh: THREE.Mesh, wallBoundingBox: THREE.Box3) => {
        holeMesh.geometry.computeBoundingBox();
        const holeBoundingBox = new THREE.Box3().setFromObject(holeMesh);

        let outerWidth = wallBoundingBox.max.z - wallBoundingBox.min.z;
        if (outerWidth < 0.8) {
            outerWidth = wallBoundingBox.max.x - wallBoundingBox.min.x;
        }
        const outerDepth = wallBoundingBox.max.y - wallBoundingBox.min.y;
        let holeWidth = (holeBoundingBox.max.z - holeBoundingBox.min.z);
        let deltaX = wallBoundingBox.min.z - holeBoundingBox.min.z;
        let sign = wallBoundingBox.min.x < 0 ? 1 : -1;
        if (holeWidth < 0.3) {
            holeWidth = (holeBoundingBox.max.x - holeBoundingBox.min.x);
            deltaX = wallBoundingBox.min.x - holeBoundingBox.min.x;
            sign = wallBoundingBox.min.z < 0 ? -1 : 1;
        }
        holeWidth = holeWidth / outerWidth;
        const holeHeight = (holeBoundingBox.max.y - holeBoundingBox.min.y) / outerDepth;
        const posX = sign * (0.5 - (holeWidth / 2)) + sign * (deltaX / outerWidth);
        const posZ = (0.5 - (holeHeight / 2)) + ((wallBoundingBox.min.y - holeBoundingBox.min.y) / outerDepth);

        const holeBox = new THREE.Mesh(new THREE.BoxGeometry(holeWidth, holeHeight, 1));
        holeBox.position.x = posX;
        holeBox.position.y = -posZ;
        holeBox.updateMatrix();

        // Perform CSG operations
        const resultMesh = CSG.subtract(mainMesh, holeBox);
        resultMesh.updateMatrix();
        resultMesh.geometry.computeBoundingBox();
        resultMesh.geometry.computeVertexNormals();
        return resultMesh;
    }

    const wallMeshWithDoorsAndWindows = useMemo(() => {
        const wallBoundingBox = new THREE.Box3().setFromObject(wallMesh!);
        const material = new THREE.MeshStandardMaterial();
        material.map = wallTexture.current;
        material.normalMap = wallNormalTexture.current;
        material.roughness = 100;
        material.metalness = 0.75;

        const box = new THREE.Mesh(
            wallMesh!.geometry,
            material
        );
        box.updateMatrix();
        let resultMesh: THREE.Mesh = box;

        if (!props.hasFurnitureModels) {
            if (doorMeshes.length > 0) {
                doorMeshes.forEach(doorMesh => {
                    resultMesh = createHoleOnWall(resultMesh, doorMesh.mesh as THREE.Mesh, wallBoundingBox);
                });
            }

            if (windowMeshes.length > 0) {
                windowMeshes.forEach(windowMesh => {
                    resultMesh = createHoleOnWall(resultMesh, windowMesh.mesh as THREE.Mesh, wallBoundingBox);
                });
            }
        }
        return resultMesh;

    }, []);


    if (!props.wallDimensions) throw Error('Missing dimensions metadata for wall :: ' + surfaceId);
    const project: Project = BuilderSelct.project();
    const textures: Texture[] = BuilderSelct.designContent().textures;
    const isSelected = BuilderSelct.activeSelection().wallId === surfaceId;
    const isPreviewMode: boolean = BuilderSelct.ui().viewMode === 'preview';
    const dragSession: DragSession = BuilderSelct.ui().dragSession;

    const boundingBox = new THREE.Box3().setFromObject(wallMesh);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    let wallWidth = props.wallDimensions[0];
    const wallHeight = props.wallDimensions[1];

    if (is2DView) {
        scene.traverse((child: any) => {
            if (child.userData.type && child.userData.type === surfaceId) {
                (child as THREE.Group).visible = true;
            }
            if (child.userData && child.userData.type && (child.userData.type === surfaceId + '_wall_seperation_lines')) {
                (child as THREE.Group).visible = false;
            }
            if (child.userData && child.userData.type && child.userData.type === surfaceId + '_wall_baseboard') {
                (child as THREE.Group).visible = false;
            }
        });
    }

    const onWallSelection = () => {
        console.log('debug- onWallSelection :: ', surfaceId);
        dispatch(builderActions.updateActiveSelection({
            wallId: surfaceId
        }));
    };

    const newWallMaterial = new THREE.MeshStandardMaterial();
    const albedoMap = useLoader(THREE.TextureLoader, './walls/sprayed-wall-texture1_albedo_optimized.png');
    const normalMap = useLoader(THREE.TextureLoader, './walls/sprayed-wall-texture1_normal-ogl_optimized.png');
    const roughnessMap = useLoader(THREE.TextureLoader, './walls/sprayed-wall-texture1_roughness_optimized.png');
    albedoMap.wrapS = THREE.RepeatWrapping;
    albedoMap.wrapT = THREE.RepeatWrapping;
    albedoMap.repeat.set(25, 25);
    normalMap.wrapS = THREE.RepeatWrapping;
    normalMap.wrapT = THREE.RepeatWrapping;
    normalMap.repeat.set(25, 25);
    roughnessMap.wrapS = THREE.RepeatWrapping;
    roughnessMap.wrapT = THREE.RepeatWrapping;
    roughnessMap.repeat.set(25, 25);
    
    newWallMaterial.map = albedoMap;
    newWallMaterial.normalMap = normalMap;
    newWallMaterial.roughnessMap = roughnessMap;

    const updateWallColor = (hexCode: string) => {
        if (meshRef.current) {
            newWallMaterial.color = new THREE.Color(hexCode);
            meshRef.current.material = newWallMaterial;

            // (meshRef.current.material as THREE.MeshStandardMaterial).map = wallTexture.current;
            // (meshRef.current.material as THREE.MeshStandardMaterial).map = normalMap;
            
            // (meshRef.current.material as THREE.MeshStandardMaterial).color = new THREE.Color(hexCode);
            // (meshRef.current.material as THREE.MeshStandardMaterial).needsUpdate = true;
        }
    }

    const updateWallTexture = () => {
        if (meshRef.current) {
            // (meshRef.current.material as THREE.MeshStandardMaterial).map = wallTexture.current;
            // (meshRef.current.material as THREE.MeshStandardMaterial).normalMap = normalMap;
            // (meshRef.current.material as THREE.MeshStandardMaterial).needsUpdate = true;

            meshRef.current.material = newWallMaterial;
        } else {
            setTextureLoaded(true);
        }
    }

    const onWallTextureLoad = (texture: any) => {
        texture.repeat.set(10, 10);
        texture.wrapS = texture.wrapT = THREE.MirroredRepeatWrapping;
        wallTexture.current = texture;

        // Normal
        // const defaultWallNormalTextureObj = textures.find(texture => {
        //     return getDefaultWallNormalTexture() === texture.id;
        // });
        // if (defaultWallNormalTextureObj) {
        //     loadTexture(defaultWallNormalTextureObj?.textureUrl, onWallNormalTextureLoaded)
        // }
        

    }
    const onWallNormalTextureLoaded = (texture: any) => {
        texture.repeat.set(10, 10);
        texture.wrapS = texture.wrapT = THREE.MirroredRepeatWrapping;
        wallNormalTexture.current = texture;

        updateWallTexture();
    }

    const getWindowOrDoorMesh = (
        mesh: THREE.Mesh,
        asset: {
            model: THREE.Group;
            offsets: RoomAssetOffsets;
        },
        color: string,
        meshMetada?: {
            [key: string]: string | number[];
        }
    ) => {
        const meshDimens: number[] = meshMetada?.['dimensions'] as number[];
        const modelBox = new THREE.Box3().setFromObject(asset.model);
        const modelBoxSize = new THREE.Vector3();
        modelBox.getSize(modelBoxSize);

        const meshBox = new THREE.Box3().setFromObject(mesh);
        const meshSize = new THREE.Vector3();
        meshBox.getSize(meshSize);


        const scaleX = meshDimens[0] / (modelBoxSize.x + asset.offsets.w);
        const scaleY = meshDimens[1] / (modelBoxSize.y + asset.offsets.h);
        let posDepthXOffset = meshSize.x < 0.5 ? mesh.position.x < 0 ? asset.offsets.z : -asset.offsets.z : 0;
        let posDepthZOffset = meshSize.z < 0.5 ? mesh.position.z < 0 ? asset.offsets.z : -asset.offsets.z : 0;

        const posWidthXOffset = meshSize.z < 0.5 ? asset.offsets.x : 0;
        const posWidthZOffset = meshSize.x < 0.5 ? -asset.offsets.x : 0;

        return (<group
            position={[
                mesh.position.x + posDepthXOffset + posWidthXOffset,
                is2DView ? wallMesh.position.y : mesh.position.y + asset.offsets.y,
                mesh.position.z + posDepthZOffset + posWidthZOffset
            ]}
            rotation={[mesh.rotation.x, mesh.rotation.y, mesh.rotation.z]}
            scale={[scaleX, is2DView ? 1 : scaleY, is2DView ? 0.15 : 1]}
        >
            {!is2DView ?
                (<primitive
                    rotation={[0, 0, 0]}
                    position={[0, 0, 0]}
                    object={asset!.model.clone()} />)
                :
                (<mesh rotation={[0, 0, 0]}
                    position={[0, 0, 0]}
                    name={mesh.name}>
                    <boxGeometry attach="geometry" args={[meshSize.z > 0.5 ? meshSize.z : meshSize.x, 0.1, 1]} />
                    <meshStandardMaterial attach="material" color={color} />
                </mesh>)
            }
        </group>);
    }

    const surfaceGroup: SurfaceGroup | undefined = project.surfaceGroups.find(surfaceGroup =>
        surfaceGroup.surfaceType === SurfaceType.WALL
    );

    if (surfaceGroup) {
        surface = surfaceGroup.surfaces.find(surface => surface.id === surfaceId);
        if (surface) {
            textureObj = textures.find(texture => {
                return surface!.products.find((placement) => placement.productId === texture.id) !== undefined;
            });
        }
    }
    if (textureObj && textureObj.type === 'color') {
        updateWallColor(textureObj.hexCode!);
        const defaultWallTextureObj = textures.find(texture => {
            return getDefaultWallTexture() === texture.id;
        });
        if (defaultWallTextureObj && currentWallTextureUrl.current !== defaultWallTextureObj.textureUrl) {
            currentWallTextureUrl.current = textureObj?.textureUrl;
            loadTexture(defaultWallTextureObj.textureUrl, onWallTextureLoad);
        }
    }
    else if (textureObj && textureObj.type === 'image' && currentWallTextureUrl.current !== textureObj?.textureUrl) {
        currentWallTextureUrl.current = textureObj?.textureUrl;
        loadTexture(textureObj?.textureUrl, onWallTextureLoad);
    } else if (!textureObj && currentWallTextureUrl.current) {
        wallTexture.current = null;
        currentWallTextureUrl.current = undefined;
        updateWallTexture();
    }

    let clickEvents: { [key: string]: (event: any) => void } = {};
    if (!isPreviewMode) {
        clickEvents = {
            onClick: onWallSelection,
        };
    }

    const planeGeometry = new THREE.PlaneGeometry(wallWidth - 0.2, wallHeight);
    const edges = new THREE.EdgesGeometry(planeGeometry);
    const wallBorderMaterial = new THREE.LineBasicMaterial({ color: 'blue', linewidth: 10 });
    const scaleZ = is2DView ? 0.1 : wallMesh.scale.z;
    const scaleY = is2DView ? 0.025 : wallMesh.scale.y;
    const wallWidthInches = Math.ceil(wallWidth * 39.3701);

    const lineOffset = props.isThinWall ? -10 : -0.1;
    const markerP1 = props.isThinWall ? -8 : -0.20;
    const markerP2 = props.isThinWall ? -13 : -0.30;
    const hiddenWallRotationX = props.isWallRotated ? -Math.PI / 2 : 0;
    if (!wallMesh) return <></>;
    return (
        <>
            <group>
                <MeshCollider type="hull">
                    <group
                        position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                        rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                        scale={[wallMesh.scale.x, scaleY, scaleZ]}
                    >
                        <mesh
                            geometry={wallMeshWithDoorsAndWindows.geometry}
                            ref={meshRef}
                            {...clickEvents}
                            userData={{ type: surfaceId }}
                            name={surfaceId}
                            castShadow={windowMeshes.length > 0}
                        />
                    </group>
                </MeshCollider>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    scale={[wallMesh.scale.x, scaleY, scaleZ]}
                >
                    <mesh
                        rotation={[hiddenWallRotationX, 0, 0]}
                        scale={[1 / wallMesh.scale.x, 1, 1 / wallMesh.scale.z]}
                        visible={true}
                        ref={hiddenMeshRef}
                        userData={{ wtype: surfaceId, wallType: 'hidden' }}
                        name={surfaceId + '_hidden'}
                    >
                        <planeGeometry attach="geometry" args={[wallWidth, wallHeight]} />
                        <meshStandardMaterial color={'red'} transparent={true} opacity={0} side={THREE.FrontSide} />
                    </mesh>
                </group>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    scale={[1, wallMesh.scale.y, 1]}
                    visible={false}
                >
                    <lineSegments
                        geometry={edges}
                        material={wallBorderMaterial}
                        rotation={[-Math.PI / 2, 0, 0]}
                        visible={isSelected && !isPreviewMode}
                        position={[0.1, 0, 0]}
                    />
                </group>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    scale={[1, scaleY, 1]}
                    visible={is2DView && wallWidthInches > 12}
                >
                    <Line
                        points={[[0.1, lineOffset, -0.25], [wallWidth / 2, lineOffset, -0.25]]}
                        color="#16A085"
                        lineWidth={2}
                    />
                    <Line
                        points={[[-0.1, lineOffset, -0.25], [-wallWidth / 2, lineOffset, -0.25]]}
                        color="#16A085"
                        lineWidth={2}
                    />
                    <Line
                        points={[[wallWidth / 2, -1, markerP1], [wallWidth / 2, -1, markerP2]]}
                        color="red"
                        lineWidth={2}
                    />
                    <Line
                        points={[[-wallWidth / 2, -1, markerP1], [-wallWidth / 2, -1, markerP2]]}
                        color="red"
                        lineWidth={2}
                    />
                </group>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    userData={{ type: surfaceId + '_wall_seperation_lines' }}
                >
                    <Line
                        points={[[-wallWidth / 2 + 0.01, -wallHeight / 2, 0], [-wallWidth / 2 + 0.01, wallHeight / 2, 0]]}
                        color="white"
                        lineWidth={2}
                    />
                    <Line
                        points={[[wallWidth / 2 - 0.01, -wallHeight / 2, 0], [wallWidth / 2 - 0.01, wallHeight / 2, 0]]}
                        color="white"
                        lineWidth={2}
                    />

                </group>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    userData={{ type: surfaceId + '_wall_baseboard' }}
                >
                    <mesh position={[0, -wallHeight / 2, 0]}
                        name={surfaceId + '_wall_baseboard'}
                        >
                        <boxGeometry attach="geometry" args={[wallWidth, 0.15, 0.05]} />
                        <meshStandardMaterial attach="material" color={'white'} />
                    </mesh>
                </group>
                <group
                    position={[wallMesh.position.x, wallMesh.position.y, wallMesh.position.z]}
                    rotation={[wallMesh.rotation.x, wallMesh.rotation.y, wallMesh.rotation.z]}
                    visible={dragSession.isActive}
                >
                    <mesh position={[0, -wallHeight / 2, 0.025]}
                        name={surfaceId + '_wall_baseboard_green'}
                    >
                        <boxGeometry attach="geometry" args={[wallWidth, 0.025, 0.025]} />
                        <meshStandardMaterial attach="material" color={'green'} />
                    </mesh>
                </group>
                <group userData={{ type: surfaceId + '_windows_doors' }}>
                    {windowMeshes.length > 0 && windowMeshes?.map((windowMesh) => (
                        getWindowOrDoorMesh(windowMesh.mesh as THREE.Mesh, windowMesh.asset, '#2471A3', windowMesh.meshMetada)))
                    }
                    {doorMeshes.length > 0 && doorMeshes?.map((doorMesh) => (
                        getWindowOrDoorMesh(doorMesh.mesh as THREE.Mesh, doorMesh.asset, '#73C6B6', doorMesh.meshMetada)))
                    }
                </group>
                <group visible={is2DView && wallWidthInches > 12}>
                    <Text
                        position={[wallMesh.position.x, wallMesh.position.y + 0.2, wallMesh.position.z]}
                        rotation={[-Math.PI / 2, 0, 0]}
                        color="black"
                        fontSize={0.15}
                        maxWidth={2}
                        lineHeight={1}
                        letterSpacing={0.02}
                    >
                        {Math.floor(wallWidthInches / 12)}{"\'"} {Math.floor(wallWidthInches % 12)}{"\""}
                    </Text>
                </group>
            </group>
        </>
    )
}

export default Wall;